import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  WalletIcon,
  DevicePhoneMobileIcon,
  BanknotesIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClockIcon,
  ArrowLeftIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'
import { walletApi, WalletOrder } from '@/api/walletApi'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import toast from 'react-hot-toast'
import { usePaymentWebSocket, PaymentStatus } from '@/hooks/usePaymentWebSocket'

function formatCurrency(amount: number, currency: string = 'KES') {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
  }).format(amount)
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('en-KE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const statusColors: Record<string, { bg: string; text: string; icon: typeof CheckCircleIcon }> = {
  UNPAID: { bg: 'bg-gray-100', text: 'text-gray-800', icon: ClockIcon },
  PENDING: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: ClockIcon },
  PAID: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircleIcon },
  FAILED: { bg: 'bg-red-100', text: 'text-red-800', icon: ExclamationCircleIcon },
  CANCELLED: { bg: 'bg-gray-100', text: 'text-gray-800', icon: ExclamationCircleIcon },
  EXPIRED: { bg: 'bg-orange-100', text: 'text-orange-800', icon: ClockIcon },
}

const quickAmounts = [500, 1000, 2000, 5000, 10000]

export default function TopUpPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [amount, setAmount] = useState<number>(0)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [showPaymentStatus, setShowPaymentStatus] = useState(false)
  const [paymentRef, setPaymentRef] = useState('')

  // WebSocket hook for real-time payment status
  const {
    status: wsStatus,
    message: wsMessage,
    isConnected,
    connect: connectWebSocket,
    disconnect: disconnectWebSocket,
    notification,
  } = usePaymentWebSocket({
    onComplete: (notif) => {
      toast.success(notif.message || 'Top-up completed successfully!')
      queryClient.invalidateQueries({ queryKey: ['myWallet'] })
      queryClient.invalidateQueries({ queryKey: ['myWalletOrders'] })
      // Auto close after showing success
      setTimeout(() => {
        setShowPaymentStatus(false)
        setPaymentRef('')
        setAmount(0)
        setPhoneNumber('')
      }, 3000)
    },
    onError: (notif) => {
      toast.error(notif.message || 'Top-up failed')
    },
  })

  // Get wallet data
  const { data: walletData, isLoading: walletLoading } = useQuery({
    queryKey: ['myWallet'],
    queryFn: () => walletApi.getMyWallet(),
  })

  // Get recent top-up orders
  const { data: ordersData, isLoading: ordersLoading } = useQuery({
    queryKey: ['myWalletOrders'],
    queryFn: () => walletApi.getMyWalletOrders({ limit: 5 }),
  })

  // Top-up mutation
  const topUpMutation = useMutation({
    mutationFn: walletApi.topUpWallet,
    onSuccess: (data) => {
      if (data.success) {
        const ref = data.data?.paymentRef || ''
        setPaymentRef(ref)
        setShowPaymentStatus(true)
        // Connect to WebSocket for real-time status updates
        if (ref) {
          connectWebSocket(ref)
        }
        toast.success(data.message || 'Payment request sent! Check your phone.')
        queryClient.invalidateQueries({ queryKey: ['myWalletOrders'] })
      } else {
        toast.error(data.message || 'Failed to initiate payment')
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to initiate payment')
    },
  })

  const closePaymentStatus = () => {
    disconnectWebSocket()
    setShowPaymentStatus(false)
    setPaymentRef('')
  }

  // Get status display info
  const getStatusDisplay = (status: PaymentStatus) => {
    switch (status) {
      case 'PENDING':
        return {
          icon: <ClockIcon className="h-16 w-16 text-yellow-500" />,
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-50',
          spinnerColor: 'border-yellow-500',
        }
      case 'PROCESSING':
        return {
          icon: <DevicePhoneMobileIcon className="h-16 w-16 text-blue-500" />,
          color: 'text-blue-600',
          bgColor: 'bg-blue-50',
          spinnerColor: 'border-blue-500',
        }
      case 'COMPLETED':
        return {
          icon: <CheckCircleIcon className="h-16 w-16 text-green-500" />,
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          spinnerColor: 'border-green-500',
        }
      case 'FAILED':
      case 'CANCELLED':
        return {
          icon: <XCircleIcon className="h-16 w-16 text-red-500" />,
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          spinnerColor: 'border-red-500',
        }
      case 'TIMEOUT':
        return {
          icon: <ExclamationTriangleIcon className="h-16 w-16 text-orange-500" />,
          color: 'text-orange-600',
          bgColor: 'bg-orange-50',
          spinnerColor: 'border-orange-500',
        }
      default:
        return {
          icon: <ClockIcon className="h-16 w-16 text-gray-500" />,
          color: 'text-gray-600',
          bgColor: 'bg-gray-50',
          spinnerColor: 'border-gray-500',
        }
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount')
      return
    }

    if (!phoneNumber || phoneNumber.length < 9) {
      toast.error('Please enter a valid phone number')
      return
    }

    topUpMutation.mutate({ amount, phoneNumber })
  }

  const wallet = walletData?.data
  const orders = ordersData?.data?.orders || []

  if (walletLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/parent/wallet')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Top Up Wallet</h1>
          <p className="text-gray-600">Add funds to your wallet via M-Pesa</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top-up Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Current Balance Card */}
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl p-6 text-white">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                <WalletIcon className="w-7 h-7" />
              </div>
              <div>
                <p className="text-primary-100 text-sm">Current Balance</p>
                <p className="text-3xl font-bold">
                  {formatCurrency(wallet?.balance || 0, wallet?.currency)}
                </p>
              </div>
            </div>
          </div>

          {/* Top-up Form Card */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Enter Top-up Details</h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Quick Amount Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Select Amount
                </label>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {quickAmounts.map((quickAmount) => (
                    <button
                      key={quickAmount}
                      type="button"
                      onClick={() => setAmount(quickAmount)}
                      className={`px-4 py-3 text-sm font-medium rounded-lg border transition-colors ${
                        amount === quickAmount
                          ? 'bg-primary-600 text-white border-primary-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {formatCurrency(quickAmount)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Or enter custom amount
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                    KES
                  </span>
                  <input
                    type="number"
                    value={amount || ''}
                    onChange={(e) => setAmount(Number(e.target.value))}
                    placeholder="0"
                    min={1}
                    max={150000}
                    className="w-full pl-14 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-lg"
                  />
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Maximum: KES 150,000
                </p>
              </div>

              {/* Phone Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  M-Pesa Phone Number
                </label>
                <div className="relative">
                  <DevicePhoneMobileIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="0712 345 678"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Enter the phone number registered with M-Pesa
                </p>
              </div>

              {/* Summary */}
              {amount > 0 && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Amount to top up</span>
                    <span className="text-xl font-bold text-gray-900">
                      {formatCurrency(amount)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-2 text-sm">
                    <span className="text-gray-500">New balance will be</span>
                    <span className="font-medium text-green-600">
                      {formatCurrency((wallet?.balance || 0) + amount)}
                    </span>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={topUpMutation.isPending || amount <= 0 || !phoneNumber}
                className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {topUpMutation.isPending ? (
                  <>
                    <LoadingSpinner size="sm" />
                    Sending payment request...
                  </>
                ) : (
                  <>
                    <BanknotesIcon className="w-5 h-5" />
                    Pay with M-Pesa
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Recent Orders Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">Recent Top-ups</h3>
            </div>
            <div className="divide-y divide-gray-100 max-h-[500px] overflow-y-auto">
              {ordersLoading ? (
                <div className="p-4 flex justify-center">
                  <LoadingSpinner size="sm" />
                </div>
              ) : orders.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  <BanknotesIcon className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                  <p className="text-sm">No top-up history</p>
                </div>
              ) : (
                orders.map((order: WalletOrder) => {
                  const StatusIcon = statusColors[order.status]?.icon || ClockIcon
                  return (
                    <div key={order._id} className="p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-gray-900">
                            {formatCurrency(order.amount, order.currency)}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {order.phoneNumber}
                          </p>
                        </div>
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${
                            statusColors[order.status]?.bg
                          } ${statusColors[order.status]?.text}`}
                        >
                          <StatusIcon className="w-3 h-3" />
                          {order.status}
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 mt-2">
                        {formatDate(order.createdAt)}
                      </p>
                      {order.mpesaReceiptNumber && (
                        <p className="text-xs text-gray-500 mt-1">
                          Receipt: {order.mpesaReceiptNumber}
                        </p>
                      )}
                    </div>
                  )
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Payment Status Overlay */}
      {showPaymentStatus && paymentRef && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl overflow-hidden">
            {/* Status Header */}
            <div className={`px-6 py-4 ${getStatusDisplay(wsStatus).bgColor}`}>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Top-up Status</h3>
                {isConnected && (
                  <span className="flex items-center text-xs text-green-600">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-1 animate-pulse"></span>
                    Live
                  </span>
                )}
              </div>
            </div>

            {/* Status Body */}
            <div className="px-6 py-8">
              <div className="text-center">
                {/* Spinner for pending/processing states */}
                {(wsStatus === 'PENDING' || wsStatus === 'PROCESSING' || wsStatus === 'IDLE') && (
                  <div className="relative mx-auto w-20 h-20 mb-6">
                    <div className={`absolute inset-0 border-4 ${getStatusDisplay(wsStatus).spinnerColor} border-t-transparent rounded-full animate-spin`}></div>
                    <div className="absolute inset-2 flex items-center justify-center">
                      {wsStatus === 'PROCESSING' ? (
                        <DevicePhoneMobileIcon className="h-8 w-8 text-blue-500" />
                      ) : (
                        <ClockIcon className="h-8 w-8 text-yellow-500" />
                      )}
                    </div>
                  </div>
                )}

                {/* Icon for final states */}
                {(wsStatus === 'COMPLETED' || wsStatus === 'FAILED' || wsStatus === 'CANCELLED' || wsStatus === 'TIMEOUT') && (
                  <div className="mb-6">
                    {getStatusDisplay(wsStatus).icon}
                  </div>
                )}

                {/* Status Text */}
                <h4 className={`text-xl font-semibold mb-2 ${getStatusDisplay(wsStatus).color}`}>
                  {wsStatus === 'IDLE' && 'Connecting...'}
                  {wsStatus === 'PENDING' && 'Waiting for Payment'}
                  {wsStatus === 'PROCESSING' && 'Check Your Phone'}
                  {wsStatus === 'COMPLETED' && 'Top-up Successful!'}
                  {wsStatus === 'FAILED' && 'Top-up Failed'}
                  {wsStatus === 'CANCELLED' && 'Top-up Cancelled'}
                  {wsStatus === 'TIMEOUT' && 'Top-up Timed Out'}
                </h4>

                <p className="text-sm text-gray-600 mb-4">
                  {wsMessage || 'Please wait while we process your top-up...'}
                </p>

                {/* Transaction details for completed payments */}
                {wsStatus === 'COMPLETED' && notification && (
                  <div className="bg-green-50 rounded-lg p-4 mt-4 text-left">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600">Amount Added:</span>
                      <span className="font-semibold text-green-600">
                        {notification.amount ? formatCurrency(notification.amount) : '-'}
                      </span>
                    </div>
                    {notification.transactionRef && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">M-Pesa Receipt:</span>
                        <span className="font-mono text-gray-900">{notification.transactionRef}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Payment reference */}
                <p className="text-xs text-gray-400 mt-4">
                  Reference: {paymentRef}
                </p>
              </div>
            </div>

            {/* Status Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-center">
              {(wsStatus === 'COMPLETED' || wsStatus === 'FAILED' || wsStatus === 'CANCELLED' || wsStatus === 'TIMEOUT') ? (
                <button
                  onClick={closePaymentStatus}
                  className={`px-6 py-2 rounded-lg text-white ${
                    wsStatus === 'COMPLETED' ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-600 hover:bg-gray-700'
                  }`}
                >
                  {wsStatus === 'COMPLETED' ? 'Done' : 'Close'}
                </button>
              ) : (
                <button
                  onClick={closePaymentStatus}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
